import { Access } from "@p0security/shared/types/access/index";

export const AwsMidcAccesses = {
  "permission-set": Access({
    description: "Assign Identity Center permission set",
    label: "Permission set",
    maturity: "ga",
    delegation: {},
    generated: {},
    parameters: {
      accountId: {
        type: "generated",
        label: "Account ID",
        description: "AWS's internal numeric ID for this account",
      } as const,
      arn: {
        type: "generated",
        label: "Permission-set ARN",
        description: "Permission-set ARN",
      } as const,
      name: {
        type: "static",
        label: "Permission-set",
        description: "Permission-set name or ARN",
      } as const,
      policyDraft: {
        type: "input",
        optional: true,
        label: "Policy draft",
        description:
          "Unhydrated policy document JSON generated at request time",
      } as const,
    },
    render: {
      locator: "accountId",
      policy: "name",
    } as const,
  }),
  policy: Access({
    description: "Attach AWS policy to user",
    label: "Policy",
    maturity: "ga",
    delegation: {},
    generated: {
      arn: { type: "string", label: "ARN of permission set" },
      name: { type: "string", label: "Name of permission set" },
    },
    parameters: {
      accountId: {
        type: "generated",
        label: "Account ID",
        description: "AWS's internal numeric ID for this account",
      } as const,
      arns: {
        type: "dynamic",
        multivalued: true,
        label: "Policy",
        description: "Managed or custom policy ARN",
      } as const,
      extends: {
        type: "static",
        label: "Extend policies",
        description: "Also include all policies from this permission set",
        optional: true,
      } as const,
      extendsName: {
        type: "generated",
        label: "Extend policies",
        description: "Also include all policies from this permission set name",
        optional: true,
      } as const,
      userName: {
        type: "generated",
        label: "User name",
        description: "Internal AWS username for this user",
      } as const,
    },
    render: {
      locator: "accountId",
      policy: "arns",
    } as const,
  }),
  resource: Access({
    description: "Policies for an AWS resource",
    label: "Resource",
    maturity: "ga",
    delegation: {},
    generated: {
      arn: { type: "string", label: "ARN of permission set or role" },
      name: { type: "string", label: "Name of permission set or role" },
      policyArn: { type: "string", label: "Generated policy ARN" },
      policyDocument: { type: "string", label: "Policy JSON" },
      policyName: { type: "string", label: "Generated policy name" },
    },
    parameters: {
      accountId: {
        type: "generated",
        label: "Account ID",
        description: "AWS's internal numeric ID for this account",
      } as const,
      arn: {
        type: "dynamic",
        label: "Resource ARN",
        description: "Resource ARN, e.g. arn:aws:s3:::{bucket_name}",
        waitForInput: true,
      } as const,
      extends: {
        type: "static",
        label: "Extend policies",
        description: "Also include all policies from this permission set",
        optional: true,
      } as const,
      extendsName: {
        type: "generated",
        label: "Extend policies",
        description: "Also include all policies from this permission set name",
        optional: true,
      } as const,
      object: {
        type: "input",
        optional: true,
        label: "File or folder",
        description: "File or folder within this resource",
      } as const,
      policies: {
        type: "dynamic",
        multivalued: true,
        label: "Policies",
        description: "Managed or custom policy names or ARNs",
      } as const,
      policyDraft: {
        type: "input",
        optional: true,
        label: "Policy draft",
        description:
          "Unhydrated policy document JSON generated at request time",
      } as const,
      service: {
        type: "generated",
        label: "Resource service",
        description: "Service that contains the AWS resource",
      } as const,
      userName: {
        type: "generated",
        label: "User name",
        description: "Internal AWS username for this user",
      } as const,
    },
    render: {
      locator: "accountId",
      policy: "policies",
      resource: "arn",
    } as const,
  }),
};

export type AwsMidcAccesses = typeof AwsMidcAccesses;
